<template>
  <div>
    <!-- Section header -->
    <div class="mb-4">
      <div class="ext-section-number">Scorecard</div>
      <h1 class="ext-section-title">Physician Scorecard</h1>
    </div>

    <!-- Two-pane layout: left rail (controls + subject list) · right panel -->
    <div class="ps-layout">

      <!-- ── LEFT RAIL: filters/search + subject list ── -->
      <aside class="ps-rail">
        <!-- Subject toggle -->
        <div class="ps-rail-group">
          <label class="ps-label">Scorecard for</label>
          <select v-model="entityKind" class="ps-select ps-full" @change="onEntityKindChange">
            <option value="Staff">Physician</option>
            <option value="Facility">Facility</option>
          </select>
        </div>

        <!-- Search -->
        <div class="ps-rail-group">
          <label class="ps-label">Search {{ subjectLabel.toLowerCase() }}</label>
          <input
            v-model="physicianSearch"
            type="text"
            class="ps-input ps-full"
            :placeholder="`Search ${subjectLabel.toLowerCase()}…`"
          />
        </div>

        <!-- Subject list -->
        <div class="ps-list">
          <div v-if="physicianLoading" class="ps-menu-msg">Searching…</div>
          <div v-else-if="!physicianResults.length" class="ps-menu-msg">No {{ subjectLabel.toLowerCase() }}s found.</div>
          <button
            v-for="p in physicianResults"
            :key="p.id"
            class="ps-list-item"
            :class="{ 'is-active': primary && primary.id === p.id }"
            @click="selectPrimary(p)"
          >
            <span class="ps-list-name">{{ p.name }}</span>
            <span v-if="p.subtitle" class="ps-list-sub">{{ p.subtitle }}</span>
          </button>
        </div>

        <!-- View controls -->
        <div class="ps-rail-controls">
          <div class="ps-rail-group">
            <label class="ps-label">Year</label>
            <select v-model.number="year" class="ps-select ps-full" @change="onYearChange">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="ps-rail-group">
            <label class="ps-label">Trend window</label>
            <select v-model.number="trendWindow" class="ps-select ps-full">
              <option :value="3">3 months</option>
              <option :value="6">6 months</option>
              <option :value="12">12 months</option>
            </select>
          </div>
          <div class="ps-rail-group">
            <label class="ps-label">Compare to</label>
            <select v-model="comparisonMode" class="ps-select ps-full" @change="onComparisonModeChange">
              <option value="none">— none —</option>
              <option value="practice">Practice average</option>
              <option value="physician">Another {{ subjectLabel.toLowerCase() }}</option>
            </select>
          </div>
          <div v-if="comparisonMode === 'physician'" class="ps-rail-group ps-physician">
            <label class="ps-label">Comparison {{ subjectLabel.toLowerCase() }}</label>
            <div class="ps-combo">
              <input
                v-model="comparePhysicianSearch"
                type="text"
                class="ps-input ps-full"
                :placeholder="comparePhysician ? comparePhysician.name : 'Search…'"
                @focus="compareMenuOpen = true"
                @input="compareMenuOpen = true"
              />
              <div v-if="compareMenuOpen" class="ps-menu" @mouseleave="compareMenuOpen = false">
                <div v-if="comparePhysicianLoading" class="ps-menu-msg">Searching…</div>
                <div v-else-if="!comparePhysicianResults.length" class="ps-menu-msg">None found.</div>
                <button
                  v-for="p in comparePhysicianResults"
                  :key="p.id"
                  class="ps-menu-item"
                  @click="selectCompare(p)"
                >
                  <span class="ps-menu-name">{{ p.name }}</span>
                </button>
              </div>
            </div>
          </div>
          <button class="ps-btn ps-full" @click="metricPickerOpen = !metricPickerOpen">
            Measures ({{ selectedMetricIds.length }})
          </button>
          <div v-if="metricPickerOpen" class="ps-measure-panel">
            <div class="ps-measure-head">
              <span>Select measures</span>
              <div>
                <button class="ps-link" @click="selectAllMetrics">All</button>
                <button class="ps-link" @click="selectedMetricIds = []">None</button>
              </div>
            </div>
            <div class="ps-measure-domains">
              <div v-for="dom in metricDomains" :key="dom.id" class="ps-measure-domain">
                <div class="ps-measure-domain-name">{{ dom.name }}</div>
                <label v-for="m in dom.metrics" :key="m.metric_id" class="ps-measure-item">
                  <input v-model="selectedMetricIds" type="checkbox" :value="m.metric_id" />
                  <span>{{ m.metric_name }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="ps-rail-exports">
            <button class="ps-btn" :disabled="!canExport" @click="exportFile('xlsx')">↓ Excel</button>
            <button class="ps-btn" :disabled="!canExport" @click="exportFile('pptx')">↓ PPTX</button>
          </div>
        </div>
      </aside>

      <!-- ── RIGHT PANEL: tabbed — Scorecard | Clinical Alerts ── -->
      <section class="ps-panel">

    <!-- Tab bar -->
    <div v-if="primary" class="ps-tabs">
      <button class="ps-tab" :class="{ 'is-active': rightTab === 'scorecard' }" @click="rightTab = 'scorecard'">Scorecard</button>
      <button v-if="entityKind === 'Staff'" class="ps-tab" :class="{ 'is-active': rightTab === 'dossiers' }" @click="rightTab = 'dossiers'">
        Clinical Alerts<span v-if="dossierTypes.length" class="ps-tab-count">{{ dossierTypes.length }}</span>
      </button>
    </div>

    <!-- Empty / loading states -->
    <div v-if="!primary" class="ps-empty">Choose a {{ subjectLabel.toLowerCase() }} from the list to build a scorecard.</div>

    <!-- ══ SCORECARD TAB ══ -->
    <template v-else-if="rightTab === 'scorecard'">
    <div v-if="loading" class="ps-empty">Building scorecard…</div>
    <div v-else-if="!scorecardRows.length" class="ps-empty">No metric data for this {{ subjectLabel.toLowerCase() }}.</div>

    <!-- Scorecard -->
    <div v-else class="ps-card">
      <div class="ps-card-head">
        <div>
          <div class="ps-card-title sensitive">{{ primary.name }}</div>
          <div class="ps-card-sub">{{ year }} · {{ scorecardRows.length }} measures</div>
        </div>
        <!-- Panel size — so a physician's rates are read in the context of how
             many lives they're attributed. Small panels make rates volatile. -->
        <div class="ps-panel-stat">
          <div class="ps-panel-num">{{ fmtNum(panelSize) }}</div>
          <div class="ps-panel-lbl">Total Lives</div>
          <div v-if="comparisonActive && comparePanelSize != null" class="ps-panel-cmp">
            vs {{ fmtNum(comparePanelSize) }} · {{ comparisonShort }}
          </div>
        </div>
        <div v-if="comparisonLabel" class="ps-compare-legend sensitive">
          <span class="ps-dot ps-dot-primary" /> {{ primary.name }}
          <span class="ps-dot ps-dot-compare" /> {{ comparisonLabel }}
        </div>
      </div>
      <!-- Small-panel caveat: rates on a tiny panel are noisy; flag it. -->
      <div v-if="panelSize != null && panelSize < smallPanelThreshold" class="ps-panel-warn">
        Small panel ({{ fmtNum(panelSize) }} lives) — rates may be volatile; read with care.
      </div>

      <table v-if="entityKind === 'Staff' || scorecardRows.length" class="ext-table ps-table">
        <thead>
          <tr>
            <th v-if="entityKind === 'Facility'">Facility</th>
            <th>Measure</th>
            <th class="num">Value</th>
            <th class="num">Numerator / Denominator</th>
            <th class="num">Target</th>
            <th class="num">Trend</th>
            <th class="ps-trend-col">{{ trendWindow }}-Month Trend</th>
            <th v-if="!isCurana" class="num">Pt Lives</th>
            <th v-if="comparisonActive" class="num">{{ comparisonShort }} Value</th>
            <th v-if="comparisonActive" class="num">Δ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in scorecardRows" :key="`${row.entity_id}-${row.metric_id}`">
            <td class="sensitive" v-if="entityKind === 'Facility'">{{ row.entity_name }}</td>
            <td class="metric-name">{{ row.metric_name }}</td>
            <td class="num" :style="{ color: rateColor(row) }">{{ fmtRate(row) }}</td>
            <td class="num">{{ row.is_count ? '—' : `${fmtNum(row.numerator)} / ${fmtNum(row.denominator)}` }}</td>
            <td class="num">{{ !row.is_count && row.target != null ? fmtTarget(row) : '—' }}</td>
            <td class="num" :style="{ color: trendColor(row) }">{{ fmtTrend(row) }}</td>
            <td class="ps-trend-cell">
              <Chart_V5
                v-if="row.spark.length > 1"
                :chart_data="sparkChartData(row)"
                :chart_config="sparkChartConfig(row)"
                :show-export="false"
                override-height="h-[132px]"
              />
              <span v-else class="ps-no-trend">Not enough history</span>
            </td>
            <td v-if="!isCurana" class="num">{{ fmtNum(row.attribution) }}</td>
            <td v-if="comparisonActive" class="num">{{ fmtRateVal(row.compareRate, row.format) }}</td>
            <td v-if="comparisonActive" class="num" :style="{ color: deltaColor(row) }">{{ fmtDelta(row) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </template>

    <!-- ══ CLINICAL ALERTS TAB ══ a LIST of provider-dossier analyses (SATs) for the selected
         provider. Each row is a config-driven SAT (Coding, Utilization, Top-5,
         …); adding a SAT adds a row here with no code change. -->
    <template v-else-if="rightTab === 'dossiers' && entityKind === 'Staff'">
    <div v-if="!dossierTypes.length" class="ps-empty">No clinical alert types configured.</div>
    <div v-else class="ps-dossiers">
      <div v-for="t in dossierTypes" :key="t.id" class="ps-dsr">
        <div class="ps-dsr-row" @click="toggleDossier(t.id)">
          <div class="ps-dsr-caret">{{ openDossierId === t.id ? '▾' : '▸' }}</div>
          <div class="ps-dsr-main">
            <div class="ps-dsr-name">{{ t.name }}</div>
            <div class="ps-dsr-desc">{{ t.description }}</div>
          </div>
          <div class="ps-dsr-status">
            <span v-if="(dossierByType[t.id] || {}).loading" class="ps-dsr-tag">Generating…</span>
            <span v-else-if="(dossierByType[t.id] || {}).data" class="ps-dsr-tag ps-dsr-ready">
              Ready<span v-if="(dossierByType[t.id] || {}).asOf"> · {{ (dossierByType[t.id]).asOf }}</span>
            </span>
            <span v-else class="ps-dsr-tag ps-dsr-none">Not generated</span>
          </div>
          <button class="ps-btn ps-dsr-btn" :disabled="(dossierByType[t.id] || {}).loading"
                  @click.stop="runDossier(t.id)">
            {{ (dossierByType[t.id] || {}).data ? 'Refresh' : 'Generate' }}
          </button>
        </div>

        <!-- Expanded dossier content -->
        <div v-if="openDossierId === t.id" class="ps-dsr-body">
          <div v-if="(dossierByType[t.id] || {}).error" class="ps-dossier-error">{{ (dossierByType[t.id]).error }}</div>
          <div v-else-if="(dossierByType[t.id] || {}).loading" class="ps-empty">Building clinical alert… (~30s)</div>
          <div v-else-if="!(dossierByType[t.id] || {}).data" class="ps-empty">No clinical alert yet — click Generate.</div>
          <div v-else>
            <p class="ps-dossier-headline">{{ (dossierByType[t.id]).data.headline }}</p>
            <div v-for="(topic, ti) in ((dossierByType[t.id]).data.discussion_topics || [])" :key="ti" class="ps-topic">
              <div class="ps-topic-head">
                <span class="ps-topic-badge" :class="playbookClass(topic.playbook)">{{ playbookLabel(topic.playbook) }}</span>
                <span class="ps-topic-title">{{ topic.title }}</span>
              </div>
              <p class="ps-topic-desc">{{ topic.pattern_description }}</p>
              <div v-for="(ex, ei) in (topic.patient_examples || [])" :key="ei" class="ps-example">
                <div class="ps-example-meta">
                  <span>{{ ex.name }}</span>
                  <span class="sensitive">MBI {{ ex.mbi }}</span>
                  <span v-if="ex.patient_status" class="ps-example-status sensitive">{{ ex.patient_status }}</span>
                  <span class="ps-example-codes">{{ ex.codes }}</span>
                  <span class="ps-example-dos">{{ ex.dos }}</span>
                </div>
                <div class="ps-example-finding">{{ ex.finding }}</div>
              </div>
              <ul v-if="topic.questions_to_explore && topic.questions_to_explore.length" class="ps-questions">
                <li v-for="(qn, qi) in topic.questions_to_explore" :key="qi">{{ qn }}</li>
              </ul>
            </div>
            <!-- Top-patients shape: ranked patient cards -->
            <div v-for="(pt, pi) in ((dossierByType[t.id]).data.patients || [])" :key="'p'+pi" class="ps-ptcard">
              <div class="ps-ptcard-head">
                <span class="ps-ptcard-rank">{{ pi + 1 }}</span>
                <span class="ps-ptcard-name sensitive">{{ pt.name || ('MBI ' + pt.mbi) }}</span>
                <span class="ps-ptcard-mbi sensitive">{{ pt.mbi }}</span>
                <span v-if="pt.patient_status" class="ps-ptcard-status sensitive">{{ pt.patient_status }}</span>
                <span v-if="pt.risk_signal" class="ps-ptcard-signal">{{ pt.risk_signal }}</span>
              </div>
              <div class="ps-ptcard-summary">{{ pt.one_line_summary }}</div>
              <div class="ps-ptcard-why"><strong>Why now:</strong> {{ pt.why_prioritized }}</div>
              <div class="ps-ptcard-step"><strong>Next step:</strong> {{ pt.suggested_next_step }}</div>
              <div v-if="pt.open_opportunities" class="ps-ptcard-opps">
                <strong>Open:</strong> {{ pt.open_opportunities }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

      </section>
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue'
import { mapGetters } from 'vuex'
import { exportBlockedInDemoMode } from '@/utils/demoSpoof'

export default {
  name: 'ExternalPhysicianScorecard',
  components: { Chart_V5 },
  props: {
    entityId: { type: Number, default: null },   // parent scope (practice/contract/tenant)
  },
  // Cross-tab deep link handled by ExternalModule (the shell owns page switching).
  data() {
    return {
      year: 2026,
      smallPanelThreshold: 30,   // below this, flag rates as volatile
      entityKind: 'Staff',       // 'Staff' (physician) or 'Facility'
      trendWindow: 12,           // sparkline/trend lookback in months (3/6/12)
      // physician selection
      primary: null,
      physicianSearch: '',
      physicianResults: [],
      physicianLoading: false,
      physicianMenuOpen: false,
      physicianDebounce: null,
      // comparison
      comparisonMode: 'none',
      comparePhysician: null,
      comparePhysicianSearch: '',
      comparePhysicianResults: [],
      comparePhysicianLoading: false,
      compareMenuOpen: false,
      compareDebounce: null,
      // metrics
      metricDomains: [],
      selectedMetricIds: [],
      metricPickerOpen: false,
      // export
      // scorecard data
      loading: false,
      primaryRows: [],       // run_multi results for primary
      primarySeries: {},     // metric_id -> time series points
      compareRows: [],       // run_multi results for comparison
      rightTab: 'scorecard', // right-panel tab: 'scorecard' | 'dossiers'
      // dossiers — a list of dossier SATs for the selected provider
      dossierTypes: [],       // available dossier SATs: [{id, name, description}]
      dossierByType: {},      // sat_id -> { loading, error, data, asOf }
      openDossierId: null,    // which dossier is expanded
    }
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    isCurana() { return this.current_tenant_slug === 'curana' },
    yearOptions() { return [2026, 2025, 2024] },
    subjectLabel() { return this.entityKind === 'Facility' ? 'Facility' : 'Physician' },
    comparisonActive() {
      return this.comparisonMode !== 'none' && this.compareRows.length > 0
    },
    comparisonLabel() {
      if (this.comparisonMode === 'practice') return 'Practice average'
      if (this.comparisonMode === 'physician' && this.comparePhysician) return this.comparePhysician.name
      return ''
    },
    comparisonShort() {
      if (this.comparisonMode === 'practice') return 'Practice'
      return 'Compare'
    },
    canExport() { return !!this.primary && this.scorecardRows.length > 0 },
    metricConfigById() {
      const map = {}
      for (const domain of this.metricDomains) {
        for (const metric of domain.metrics) map[metric.metric_id] = metric
      }
      return map
    },
    panelSize() {
      if (this.isCurana) {
        const totalLives = this.primaryRows.find(r => Number(r.metric_id) === 606)
        return totalLives ? (totalLives.numerator ?? totalLives.attribution ?? null) : null
      }
      const vals = this.primaryRows.map(r => r.attribution || 0)
      return vals.length ? Math.max(...vals) : null
    },
    comparePanelSize() {
      if (this.isCurana) {
        const totalLives = this.compareRows.find(r => Number(r.metric_id) === 606)
        return totalLives ? (totalLives.numerator ?? totalLives.attribution ?? null) : null
      }
      const vals = this.compareRows.map(r => r.attribution || 0)
      return vals.length ? Math.max(...vals) : null
    },
    // Merge primary metric results + series + comparison into display rows.
    scorecardRows() {
      // Key by entity+metric: at Facility grain there is a row per facility.
      const compareByMetric = {}
      for (const r of this.compareRows) compareByMetric[`${r.entity_id}-${r.metric_id}`] = r
      return this.primaryRows
        .filter(r => !this.isCurana || this.selectedMetricIds.some(id => Number(id) === Number(r.metric_id)))
        .map(r => {
          const dc = r.display_config || {}
          const metricConfig = this.metricConfigById[r.metric_id] || {}
          const aggregationType = metricConfig.aggregation_type || 'percentage'
          const isRate = ['percentage', 'ratio', 'sum_ratio', 'inverse_count'].includes(aggregationType)
          const isCount = aggregationType === 'count'
          const denom = r.denominator || 0
          const rate = isCount ? (r.numerator || 0) : (denom > 0 ? (r.numerator || 0) / denom : null)
          const cmp = compareByMetric[`${r.entity_id}-${r.metric_id}`]
          const cmpDenom = cmp?.denominator || 0
          const compareRate = isCount
            ? (cmp?.numerator ?? null)
            : (cmpDenom > 0 ? (cmp.numerator || 0) / cmpDenom : null)
          // Window the series to the last N months, then derive the trend from
          // the WINDOWED points so the arrow matches the visible sparkline
          // (rather than the backend's full-series slope).
          const spark = this.monthlyTrendSeries(
            this.windowedSeries(this.primarySeries[`${r.entity_id}-${r.metric_id}`] || [])
          )
          const trend_slope = this.seriesSlope(spark)
          const format = dc.format || (isRate ? 'percentage' : (isCount ? 'count' : 'decimal'))
          const isCurrency = format === 'currency'
          const isPer1k = format === 'per_1k'
          return {
            metric_id: r.metric_id,
            metric_name: r.metric_name,
            entity_id: r.entity_id,
            entity_name: r.entity_name,
            numerator: r.numerator || 0,
            denominator: denom,
            attribution: r.attribution || 0,
            rate,
            target: r.target_rate ?? dc.target ?? null,
            trend_slope,
            aggregation_type: aggregationType,
            is_rate: isRate && !isCurrency && !isPer1k,
            is_count: isCount,
            is_currency: isCurrency,
            is_per_1k: isPer1k,
            format,
            higher_is_better: dc.higher_is_better !== false,
            spark,
            compareRate,
            // fields the exporter expects
            display_config: dc,
          }
        })
        // Facility grain returns a row per facility per metric — group by
        // facility so the breakdown reads down the page.
        .sort((a, b) =>
          (a.entity_name || '').localeCompare(b.entity_name || '')
          || (a.metric_name || '').localeCompare(b.metric_name || ''))
    },
  },
  watch: {
    entityId() { this.resetPhysician(); this.loadMetricDomains() },
    physicianSearch() {
      clearTimeout(this.physicianDebounce)
      this.physicianDebounce = setTimeout(() => this.searchPhysicians(this.physicianSearch, 'primary'), 250)
    },
    comparePhysicianSearch() {
      clearTimeout(this.compareDebounce)
      this.compareDebounce = setTimeout(() => this.searchPhysicians(this.comparePhysicianSearch, 'compare'), 250)
    },
    selectedMetricIds() { if (this.primary) this.loadScorecard() },
  },
  mounted() {
    this.loadMetricDomains()
    this.loadDossierTypes()
    this.searchPhysicians('', 'primary')
  },
  methods: {
    // ── physician search (Staff entities under the scope) ──
    async searchPhysicians(q, which) {
      const setLoading = v => { which === 'primary' ? this.physicianLoading = v : this.comparePhysicianLoading = v }
      setLoading(true)
      try {
        const params = { entity_type_name: this.entityKind, page_size: 25 }
        if (this.entityId) params.ancestor_ids = this.entityId
        if (q && q.trim()) params.name = q.trim()
        const { data } = await this.$axios.get('/api/entities/', { params })
        const results = (data.results || data || []).map(e => ({
          id: e.id, name: e.name,
          entityType: e.entity_type_name || e.entity_type?.type_name || this.entityKind,
          subtitle: e.entity_type_name || '',
        }))
        if (which === 'primary') this.physicianResults = results
        else this.comparePhysicianResults = results
      } catch {
        if (which === 'primary') this.physicianResults = []
        else this.comparePhysicianResults = []
      } finally {
        setLoading(false)
      }
    },
    selectPrimary(p) {
      this.primary = p
      // Keep the search + result list intact so the rail stays stable.
      this.loadScorecard()
      this.loadDossiers()
    },
    selectCompare(p) {
      this.comparePhysician = p
      this.comparePhysicianSearch = ''
      this.compareMenuOpen = false
      this.loadComparison()
    },
    resetPhysician() {
      this.primary = null; this.primaryRows = []; this.primarySeries = {}
      this.comparePhysician = null; this.compareRows = []
      this.dossierByType = {}; this.openDossierId = null
    },

    // ── dossiers (a LIST of dossier SATs for the selected provider) ──
    async loadDossierTypes() {
      // The catalog of dossier SATs — each is a config-driven analysis, not code.
      try {
        const { data } = await this.$axios.get('/api/personal-analyst/provider-dossiers/types/')
        this.dossierTypes = data || []
      } catch {
        this.dossierTypes = []
      }
    },
    async loadDossiers() {
      // For the selected provider, load the latest of EACH dossier SAT.
      if (!this.primary || this.entityKind !== 'Staff') { this.dossierByType = {}; return }
      this.dossierByType = {}; this.openDossierId = null
      for (const t of this.dossierTypes) {
        this.dossierByType = { ...this.dossierByType, [t.id]: { loading: false, error: null, data: null, asOf: null } }
        this.fetchDossier(t.id)
      }
    },
    async fetchDossier(satId) {
      try {
        const { data } = await this.$axios.get('/api/personal-analyst/provider-dossiers/latest/', {
          params: { entity_id: this.primary.id, analysis_type: satId, year: this.year },
        })
        const slot = this.dossierByType[satId] || {}
        if (data && data.response_json) {
          slot.data = data.response_json; slot.asOf = (data.created_at || '').slice(0, 10)
        } else { slot.data = null; slot.asOf = null }
        this.dossierByType = { ...this.dossierByType, [satId]: { ...slot, loading: false } }
      } catch {
        this.dossierByType = { ...this.dossierByType, [satId]: { loading: false, error: null, data: null, asOf: null } }
      }
    },
    async runDossier(satId) {
      const slot = this.dossierByType[satId] || {}
      this.dossierByType = { ...this.dossierByType, [satId]: { ...slot, loading: true, error: null } }
      try {
        const { data } = await this.$axios.post('/api/personal-analyst/provider-dossiers/run/', {
          entity_id: this.primary.id, analysis_type: satId, year: this.year,
        })
        const s = this.dossierByType[satId] || {}
        if (data && data.response_json) {
          this.dossierByType = { ...this.dossierByType, [satId]: { ...s, loading: false, data: data.response_json, asOf: (data.created_at || '').slice(0, 10) } }
          this.openDossierId = satId
        } else {
          this.dossierByType = { ...this.dossierByType, [satId]: { ...s, loading: false, error: (data && data.error) || 'Failed to generate.' } }
        }
      } catch (e) {
        const s = this.dossierByType[satId] || {}
        this.dossierByType = { ...this.dossierByType, [satId]: { ...s, loading: false, error: e?.response?.data?.error || 'Failed to generate dossier.' } }
      }
    },
    toggleDossier(satId) {
      this.openDossierId = this.openDossierId === satId ? null : satId
    },
    // Provider-facing badge labels. The keys come from the dossier SAT output
    // schema; the labels are plain clinical language a physician reads at a
    // glance (Jennifer/Curana feedback 2026-07-29 — internal jargon like
    // "TOC Gap" / "Recurrent Acute" isn't legible to providers).
    playbookLabel(pb) {
      return {
        unspecified_to_specified: 'Add Specificity',
        awv_missed_capture: 'Wellness Visit Opportunity',
        carried_but_dropped: 'Chronic Not Re-documented',
        avoidable_ed: 'Avoidable ER Visit',
        readmission_gap: 'Post-Discharge Follow-Up',
        recurrent_acute: 'Frequent Hospital Visits',
        other: 'Pattern',
      }[pb] || 'Pattern'
    },
    playbookClass(pb) {
      return {
        unspecified_to_specified: 'pb-blue',
        awv_missed_capture: 'pb-amber',
        carried_but_dropped: 'pb-red',
        avoidable_ed: 'pb-amber',
        readmission_gap: 'pb-red',
        recurrent_acute: 'pb-blue',
      }[pb] || 'pb-gray'
    },
    onComparisonModeChange() {
      this.compareRows = []
      if (this.comparisonMode === 'practice') this.loadComparison()
      else if (this.comparisonMode === 'none') this.comparePhysician = null
    },
    onYearChange() {
      this.loadScorecard()
      this.loadDossiers()
    },
    onEntityKindChange() {
      // Switching Physician↔Facility clears the current subject + comparison and
      // reloads the picker for the new entity type.
      this.resetPhysician()
      this.physicianSearch = ''
      this.comparePhysicianSearch = ''
      this.comparePhysician = null
      this.comparePhysicianResults = []
      this.searchPhysicians('', 'primary')
    },

    // ── metric catalog ──
    async loadMetricDomains() {
      try {
        const { data } = await this.$axios.get('/api/performancedomains/')
        const domains = (data || []).map(d => ({
          id: d.id,
          name: d.name,
          metrics: (d.composable_metrics || d.metrics || [])
            .filter(m => m.metric_id || m.id)
            .map(m => ({
              metric_id: m.metric_id || m.id,
              metric_name: m.metric_name || m.name,
              aggregation_type: m.aggregation_type,
              display_config: m.display_config || {},
            })),
        })).filter(d => d.metrics.length)
        this.metricDomains = domains
        // Sensible default: first up-to-8 metrics across domains.
        if (!this.selectedMetricIds.length) {
          const all = domains.flatMap(d => d.metrics.map(m => m.metric_id))
          this.selectedMetricIds = all.slice(0, 8)
        }
      } catch {
        this.metricDomains = []
      }
    },
    selectAllMetrics() {
      this.selectedMetricIds = this.metricDomains.flatMap(d => d.metrics.map(m => m.metric_id))
    },

    livesMetricIds() {
      if (!this.isCurana) return this.selectedMetricIds
      const ids = [...this.selectedMetricIds]
      if (!ids.includes(606)) ids.push(606)
      return ids
    },
    async loadScorecard() {
      if (!this.primary || !this.selectedMetricIds.length) { this.primaryRows = []; return }
      this.loading = true
      try {
        const base = {
          metric_ids: this.livesMetricIds(),
          entity_type: this.primary.entityType || 'Staff',
          entity_id: this.primary.id,
          observation_year: this.year,
          group_by_contract: false,
        }
        const [snapshot, series] = await Promise.all([
          this.$axios.post('/api/impact_analytics/composable-metrics/run_multi/', { ...base, time_series: false }),
          this.$axios.post('/api/impact_analytics/composable-metrics/run_multi/', { ...base, time_series: true }),
        ])
        const snapshotResults = snapshot.data?.results || []
        const seriesResults = series.data?.results || []
        this.primaryRows = this.scopeSubjectResults(snapshotResults, this.primary)
        this.primarySeries = this.buildSeries(this.scopeSubjectResults(seriesResults, this.primary))
      } catch {
        this.primaryRows = []; this.primarySeries = {}
      } finally {
        this.loading = false
      }
      if (this.comparisonActive || this.comparisonMode !== 'none') this.loadComparison()
    },
    scopeSubjectResults(results, subject) {
      if (!subject || subject.entityType !== 'Facility') return results || []
      return (results || []).filter(r => Number(r.entity_id) === Number(subject.id))
    },
    async loadComparison() {
      if (this.comparisonMode === 'none' || !this.selectedMetricIds.length) { this.compareRows = []; return }
      let compareEntityId = null
      let compareEntityType = null
      if (this.comparisonMode === 'practice') {
        // Practice average = the parent scope entity. Resolve its type once.
        compareEntityId = this.entityId
        compareEntityType = await this.resolveEntityType(this.entityId)
      } else if (this.comparisonMode === 'physician' && this.comparePhysician) {
        compareEntityId = this.comparePhysician.id
        compareEntityType = this.comparePhysician.entityType || 'Staff'
      }
      if (!compareEntityId || !compareEntityType) { this.compareRows = []; return }
      try {
        const { data } = await this.$axios.post('/api/impact_analytics/composable-metrics/run_multi/', {
          metric_ids: this.livesMetricIds(),
          entity_type: compareEntityType,
          entity_id: compareEntityId,
          observation_year: this.year, time_series: false, group_by_contract: false,
        })
        const results = data?.results || []
        this.compareRows = this.scopeSubjectResults(results, {
          id: compareEntityId,
          entityType: compareEntityType,
        })
      } catch {
        this.compareRows = []
      }
    },
    async resolveEntityType(id) {
      if (!id) return null
      if (this._entityTypeCache && this._entityTypeCache[id]) return this._entityTypeCache[id]
      try {
        const { data } = await this.$axios.get(`/api/entities/${id}/`)
        const t = data.entity_type_name || data.entity_type?.type_name || 'Practice'
        this._entityTypeCache = { ...(this._entityTypeCache || {}), [id]: t }
        return t
      } catch {
        return 'Practice'
      }
    },
    // Keep only points within the selected trend window (last N months relative
    // to the latest observation). Series are already date-sorted ascending.
    windowedSeries(points) {
      if (!points || !points.length) return []
      const last = points[points.length - 1].date
      const cutoff = new Date(last)
      cutoff.setMonth(cutoff.getMonth() - this.trendWindow)
      const iso = cutoff.toISOString().slice(0, 10)
      const win = points.filter(p => p.date >= iso)
      // Guarantee at least 2 points so the sparkline/slope render meaningfully.
      return win.length >= 2 ? win : points.slice(-2)
    },
    monthlyTrendSeries(points) {
      const byMonth = {}
      for (const point of (points || [])) {
        const month = String(point.date || '').slice(0, 7)
        if (!month) continue
        if (!byMonth[month] || point.date > byMonth[month].date) byMonth[month] = point
      }
      return Object.values(byMonth).sort((a, b) => a.date.localeCompare(b.date))
    },
    // Trend = change from the first to the last point of the windowed series
    // (positive = rate rose over the window). Matches the arrow's "Δ over window"
    // reading rather than a regression slope.
    seriesSlope(points) {
      if (!points || points.length < 2) return null
      return points[points.length - 1].value - points[0].value
    },
    // Collapse time_series results into per-metric [{date, rate}] for sparklines.
    buildSeries(results) {
      const byMetric = {}
      for (const r of results) {
        // Key by entity+metric so each facility gets its own trend line.
        const mid = `${r.entity_id}-${r.metric_id}`
        const d = (r.observation_date || '').slice(0, 10)
        if (!r.metric_id || !d) continue
        if (!byMetric[mid]) byMetric[mid] = {}
        // aggregate across contracts per date
        const agg = byMetric[mid][d] || { num: 0, denom: 0 }
        agg.num += r.numerator || 0
        agg.denom += r.denominator || 0
        byMetric[mid][d] = agg
      }
      const out = {}
      for (const mid of Object.keys(byMetric)) {
        out[mid] = Object.keys(byMetric[mid]).sort().map(d => {
          const a = byMetric[mid][d]
          const metricId = Number(mid.split('-').pop())
          const aggregationType = (this.metricConfigById[metricId] || {}).aggregation_type
          return {
            date: d,
            value: aggregationType === 'count'
              ? a.num
              : (a.denom > 0 ? a.num / a.denom : 0),
          }
        })
      }
      return out
    },

    // ── export (reuse backend export_scorecard) ──
    async exportFile(format) {
      if (exportBlockedInDemoMode() || !this.canExport) return
      // Send the SAME fields the on-screen physician scorecard renders, so the
      // export (physician_scorecard layout) mirrors the screen 1:1: Rate,
      // Num/Denom, Target, Trend, N-Mo sparkline (the windowed `spark` series),
      // Pt Lives (attribution), and comparison Rate + Δ when active.
      const rows = this.scorecardRows.map(r => ({
        metric_name: r.metric_name,
        numerator: r.numerator,
        denominator: r.denominator,
        format: r.format,
        rate: r.rate,
        target: r.target,
        trend_slope: r.trend_slope,
        higher_is_better: r.higher_is_better,
        attribution: r.attribution,
        spark: r.spark,
        // comparison columns, only meaningful when a comparison is active
        ...(this.comparisonActive ? { compareRate: r.compareRate } : {}),
      }))
      try {
        const resp = await this.$axios.post(
          '/api/impact_analytics/composable-metrics/export_scorecard/',
          {
            rows,
            entity_name: this.primary.name,
            observation_year: this.year,
            format,
            layout: 'physician_scorecard',
          },
          { responseType: 'blob' },
        )
        const blob = new Blob([resp.data])
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `physician_scorecard_${this.primary.name.replace(/[^a-z0-9]+/gi, '_')}.${format}`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Export failed', e)
      }
    },

    // ── formatting ──
    sparkChartData(row) {
      return {
        labels: row.spark.map(p => p.date),
        series: [{
          values: row.spark.map(p => p.value),
          config: {
            label: row.metric_name,
            type: 'bar',
            yAxis: 'left',
            offset: 0,
            backgroundColor: '#4C6E97',
          },
        }],
      }
    },
    sparkDomain(row) {
      const values = row.spark.map(p => Number(p.value)).filter(Number.isFinite)
      if (!values.length) return { min: 0, max: 1 }
      const dataMin = Math.min(...values)
      const dataMax = Math.max(...values)
      const naturalSpan = row.is_rate ? 0.1 : Math.max(Math.abs(dataMax) * 0.1, 1)
      const span = Math.max(dataMax - dataMin, naturalSpan)
      const pad = span * 0.2
      return {
        min: 0,
        max: row.is_rate ? Math.min(1, dataMax + pad) : Math.ceil(dataMax + pad),
      }
    },
    sparkChartConfig(row) {
      const domain = this.sparkDomain(row)
      const yFormat = row.format === 'currency'
        ? value => '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
        : row.format === 'per_1k'
        ? value => `${Math.round(value)}/1k`
        : row.is_rate
        ? value => `${(Number(value) * 100).toFixed(0)}%`
        : value => Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
      return {
        chartType: 'vertical_bar',
        margin: { top: 8, right: 12, bottom: 28, left: 42 },
        xAxis: {
          labelRotation: 0,
          fontSize: 9,
          tickFormat: value => {
            const d = new Date(`${String(value).slice(0, 10)}T00:00:00`)
            return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
          },
        },
        yAxes: [{
          position: 'left',
          fontSize: 9,
          min: domain.min,
          max: domain.max,
          tickFormat: yFormat,
        }],
        legend: { display: false },
        formatting: { percentage: row.is_rate, currency: row.format === 'currency', sig_figs: row.is_rate ? 1 : 0 },
        hideDataLabels: true,
        hideGridLines: false,
      }
    },
    fmtRate(row) { return this.fmtRateVal(row.rate, row.format) },
    fmtRateVal(rate, format) {
      if (rate == null) return '—'
      if (format === 'percentage') return `${(rate * 100).toFixed(1)}%`
      if (format === 'currency') return '$' + Math.round(rate).toLocaleString('en-US')
      if (format === 'per_1k') return `${Math.round(rate)}/1k`
      if (format === 'count' || format === 'number') return this.fmtNum(rate)
      return rate.toFixed(2)
    },
    fmtTarget(row) {
      if (row.target == null) return '—'
      if (row.format === 'currency') return '$' + Math.round(row.target).toLocaleString('en-US')
      if (row.format === 'per_1k') return `${Math.round(row.target)}/1k`
      if (row.format === 'percentage') return this.fmtPct(row.target)
      return String(row.target)
    },
    fmtPct(v) { return v == null ? '—' : `${(v * 100).toFixed(0)}%` },
    fmtNum(v) { return v == null ? '—' : Number(v).toLocaleString('en-US') },
    fmtTrend(row) {
      if (row.trend_slope == null) return '—'
      const arrow = row.trend_slope > 0 ? '▲' : row.trend_slope < 0 ? '▼' : '—'
      if (row.is_count) return `${arrow} ${this.fmtNum(Math.abs(row.trend_slope))}`
      if (row.format === 'currency') return `${arrow} $${Math.round(Math.abs(row.trend_slope)).toLocaleString('en-US')}`
      if (row.format === 'per_1k') return `${arrow} ${Math.round(Math.abs(row.trend_slope))}/1k`
      return `${arrow} ${Math.abs(row.trend_slope * 100).toFixed(1)} pp`
    },
    fmtDelta(row) {
      if (row.rate == null || row.compareRate == null) return '—'
      if (row.format === 'percentage') {
        const d = (row.rate - row.compareRate) * 100
        const sign = d > 0 ? '+' : ''
        return `${sign}${d.toFixed(1)}pp`
      }
      if (row.format === 'currency') {
        const d = row.rate - row.compareRate
        const sign = d > 0 ? '+$' : d < 0 ? '-$' : '$'
        return `${sign}${Math.round(Math.abs(d)).toLocaleString('en-US')}`
      }
      if (row.format === 'per_1k') {
        const d = row.rate - row.compareRate
        const sign = d > 0 ? '+' : ''
        return `${sign}${Math.round(d)}/1k`
      }
      const d = row.rate - row.compareRate
      const sign = d > 0 ? '+' : ''
      return `${sign}${d.toFixed(1)}`
    },
    rateColor(row) {
      if (row.rate == null || row.target == null) return '#221F1C'
      const good = row.higher_is_better ? row.rate >= row.target : row.rate <= row.target
      return good ? '#166534' : '#9f1239'
    },
    trendColor(row) {
      if (row.trend_slope == null || row.trend_slope === 0) return '#7D7770'
      const improving = row.higher_is_better ? row.trend_slope > 0 : row.trend_slope < 0
      return improving ? '#166534' : '#9f1239'
    },
    deltaColor(row) {
      if (row.rate == null || row.compareRate == null) return '#7D7770'
      const better = row.higher_is_better ? row.rate > row.compareRate : row.rate < row.compareRate
      return better ? '#166534' : '#9f1239'
    },
  },
}
</script>

<style scoped>
/* ── Two-pane layout ── */
.ps-layout { display: flex; gap: 18px; align-items: flex-start; }
.ps-rail { flex: 0 0 264px; width: 264px; display: flex; flex-direction: column; gap: 12px;
  position: sticky; top: 12px; max-height: calc(100vh - 90px); }
.ps-panel { flex: 1 1 auto; min-width: 0; }
.ps-panel > .ps-empty { display: flex; align-items: center; justify-content: center; }
.ps-rail-group { display: flex; flex-direction: column; gap: 3px; }
.ps-full { width: 100%; box-sizing: border-box; }
.ps-list { border: 1px solid #E8E3DC; border-radius: 8px; background: #fff; overflow-y: auto;
  flex: 1 1 auto; min-height: 160px; max-height: 42vh; }
.ps-list-item { display: flex; flex-direction: column; align-items: flex-start; width: 100%;
  padding: 7px 12px; font-size: 12px; color: #221F1C; background: none; border: none;
  border-bottom: 1px solid #F5F2ED; cursor: pointer; text-align: left; }
.ps-list-item:hover { background: #FAF9F6; }
.ps-list-item.is-active { background: #EFF4FA; border-left: 3px solid #4C6E97; padding-left: 9px; }
.ps-list-name { font-weight: 500; }
.ps-list-sub { font-size: 10px; color: #A9A29A; margin-top: 1px; }
.ps-rail-controls { display: flex; flex-direction: column; gap: 10px; border-top: 1px solid #F2EEE8; padding-top: 12px; }
.ps-rail-exports { display: flex; gap: 8px; }
.ps-rail-exports .ps-btn { flex: 1 1 auto; }
.ps-controls { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.ps-ctrl-group { display: flex; flex-direction: column; gap: 3px; }
.ps-ctrl-spacer { flex: 1 1 auto; }
.ps-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #7D7770; font-weight: 600; }
.ps-input, .ps-select {
  font-size: 12px; border: 1px solid #E8E3DC; border-radius: 6px; padding: 5px 8px;
  background: #fff; color: #221F1C; min-width: 160px;
}
.ps-input:focus, .ps-select:focus { outline: none; border-color: #CFC8BE; }
.ps-physician { position: relative; }
.ps-combo { position: relative; }
.ps-menu {
  position: absolute; top: calc(100% + 2px); left: 0; z-index: 30; width: 280px;
  background: #fff; border: 1px solid #E8E3DC; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08); max-height: 320px; overflow-y: auto;
}
.ps-menu-msg { padding: 10px 12px; font-size: 12px; color: #A9A29A; }
.ps-menu-item {
  display: flex; flex-direction: column; align-items: flex-start; width: 100%;
  padding: 7px 12px; font-size: 12px; color: #221F1C; background: none; border: none;
  cursor: pointer; text-align: left;
}
.ps-menu-item:hover { background: #FAF9F6; }
.ps-menu-name { font-weight: 500; }
.ps-menu-sub { font-size: 10px; color: #A9A29A; }
.ps-btn {
  font-size: 11.5px; color: #4A4540; background: #fff; border: 1px solid #E8E3DC;
  border-radius: 6px; padding: 6px 12px; cursor: pointer;
}
.ps-btn:hover:not(:disabled) { border-color: #CFC8BE; background: #FAF9F6; }
.ps-btn:disabled { opacity: 0.5; cursor: default; }
.ps-link { font-size: 11px; color: #4C6E97; background: none; border: none; cursor: pointer; margin-left: 8px; }

.ps-measure-panel { border: 1px solid #E8E3DC; border-radius: 8px; padding: 10px 12px; background: #FDFCFA; max-height: 320px; overflow-y: auto; }
.ps-measure-head { display: flex; justify-content: space-between; align-items: center; font-size: 12px; font-weight: 600; color: #221F1C; margin-bottom: 8px; }
.ps-measure-domains { display: flex; flex-direction: column; gap: 10px; }
.ps-measure-domain-name { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #7D7770; margin-bottom: 4px; font-weight: 600; }
.ps-measure-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #221F1C; padding: 2px 0; cursor: pointer; }

.ps-empty { padding: 48px; text-align: center; color: #A9A29A; font-size: 13px; }
.ps-card { border: 1px solid #F2EEE8; border-radius: 10px; overflow-x: auto; background: #fff; }
.ps-card-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #F2EEE8; }
.ps-card-title { font-size: 15px; font-weight: 600; color: #221F1C; }
.ps-card-sub { font-size: 11px; color: #A9A29A; margin-top: 1px; }
.ps-panel-stat { text-align: right; padding-right: 18px; }
.ps-panel-num { font-size: 20px; font-weight: 700; color: #221F1C; line-height: 1; }
.ps-panel-lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #7D7770; font-weight: 600; margin-top: 2px; }
.ps-panel-cmp { font-size: 10px; color: #A9A29A; margin-top: 2px; }
.ps-panel-warn { padding: 6px 18px; font-size: 11px; color: #92400e; background: #fffbeb; border-bottom: 1px solid #F2EEE8; }
.ps-compare-legend { font-size: 11px; color: #7D7770; display: flex; align-items: center; gap: 6px; }
.ps-dot { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
.ps-dot-primary { background: #4C6E97; }
.ps-dot-compare { background: #C99A5B; margin-left: 8px; }
.ps-table { white-space: nowrap; min-width: 980px; }
.ps-table td, .ps-table th { vertical-align: middle; }
.ps-table tbody tr { height: 154px; }
.ps-table tbody td { padding-top: 10px; padding-bottom: 10px; }
.ps-trend-col { width: 360px; min-width: 360px; }
.ps-trend-cell { width: 360px; min-width: 360px; padding: 0 8px !important; }
.ps-no-trend { display: inline-flex; width: 100%; min-height: 80px; align-items: center;
  justify-content: center; color: #A9A29A; font-size: 11px; }

/* ── Right-panel tabs ── */
.ps-tabs { display: flex; gap: 4px; border-bottom: 1px solid #E8E3DC; margin-bottom: 14px; }
.ps-tab { font-size: 13px; font-weight: 500; color: #7D7770; background: none; border: none;
  border-bottom: 2px solid transparent; padding: 8px 14px; cursor: pointer; margin-bottom: -1px; }
.ps-tab:hover { color: #221F1C; }
.ps-tab.is-active { color: #221F1C; border-bottom-color: #4C6E97; font-weight: 600; }
.ps-tab-count { display: inline-block; margin-left: 6px; font-size: 10px; font-weight: 600;
  background: #EFF4FA; color: #4C6E97; border-radius: 8px; padding: 1px 6px; }

/* ── Dossiers list ── */
.ps-dossiers { margin-top: 0; }
.ps-dsr { border: 1px solid #F2EEE8; border-radius: 10px; background: #fff; margin-bottom: 8px; overflow: hidden; }
.ps-dsr-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; cursor: pointer; }
.ps-dsr-row:hover { background: #FAF9F6; }
.ps-dsr-caret { font-size: 11px; color: #A9A29A; width: 12px; }
.ps-dsr-main { flex: 1 1 auto; }
.ps-dsr-name { font-size: 13px; font-weight: 600; color: #221F1C; }
.ps-dsr-desc { font-size: 11px; color: #A9A29A; margin-top: 1px; }
.ps-dsr-status { flex: 0 0 auto; }
.ps-dsr-tag { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 10px; background: #f3f4f6; color: #6b7280; white-space: nowrap; }
.ps-dsr-ready { background: #ecfdf5; color: #059669; }
.ps-dsr-none { background: #f9fafb; color: #9ca3af; }
.ps-dsr-btn { flex: 0 0 auto; }
.ps-dsr-body { padding: 6px 16px 16px; border-top: 1px solid #F2EEE8; }
.ps-dossier-error { padding: 10px 0; font-size: 12px; color: #9f1239; }
.ps-dossier-headline { font-size: 13px; font-weight: 600; color: #221F1C; margin: 8px 0 14px; line-height: 1.4; }
.ps-topic { border-top: 1px solid #F2EEE8; padding: 12px 0; }
.ps-topic:first-of-type { border-top: none; }
.ps-topic-head { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.ps-topic-badge { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; padding: 2px 7px; border-radius: 10px; white-space: nowrap; }
.pb-blue { background: #eff6ff; color: #1d4ed8; }
.pb-amber { background: #fffbeb; color: #b45309; }
.pb-red { background: #fef2f2; color: #b91c1c; }
.pb-gray { background: #f3f4f6; color: #4b5563; }
.ps-topic-title { font-size: 13px; font-weight: 600; color: #221F1C; }
.ps-topic-desc { font-size: 12px; color: #4A4540; margin: 0 0 8px; line-height: 1.45; }
.ps-example { background: #FAF9F6; border: 1px solid #F2EEE8; border-radius: 6px; padding: 8px 10px; margin-bottom: 6px; }
.ps-example-meta { display: flex; flex-wrap: wrap; gap: 12px; font-size: 10.5px; color: #7D7770; margin-bottom: 3px; }
.ps-example-codes { font-family: ui-monospace, monospace; color: #4C6E97; }
.ps-example-status { font-weight: 600; color: #166534; background: #DCFCE7; border-radius: 3px; padding: 1px 5px; }
.ps-example-finding { font-size: 12px; color: #221F1C; line-height: 1.4; }
.ps-questions { margin: 8px 0 0; padding-left: 18px; }
.ps-questions li { font-size: 11.5px; color: #6b5f52; line-height: 1.5; font-style: italic; }

/* ── Top-patient cards ── */
.ps-ptcard { border-top: 1px solid #F2EEE8; padding: 12px 0; }
.ps-ptcard:first-of-type { border-top: none; }
.ps-ptcard-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.ps-ptcard-rank { flex: 0 0 20px; width: 20px; height: 20px; border-radius: 10px; background: #4C6E97;
  color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
.ps-ptcard-name { font-size: 13px; font-weight: 600; color: #221F1C; }
.ps-ptcard-mbi { font-size: 10px; color: #A9A29A; font-family: ui-monospace, monospace; }
.ps-ptcard-status { font-size: 10px; font-weight: 600; color: #166534; background: #DCFCE7;
  border-radius: 3px; padding: 2px 6px; white-space: nowrap; }
.ps-ptcard-summary { font-size: 12px; color: #4A4540; line-height: 1.45; margin-bottom: 5px; }
.ps-ptcard-why { font-size: 12px; color: #221F1C; line-height: 1.45; margin-bottom: 3px; }
.ps-ptcard-step { font-size: 12px; color: #166534; line-height: 1.45; }
.ps-ptcard-opps { font-size: 11px; color: #6B655E; line-height: 1.4; margin-top: 4px; }
/* Risk driver is informational, never alarm-red — the card is already ranked. */
.ps-ptcard-signal { font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .03em; color: #4C6E97; background: #EEF2F7; border-radius: 3px;
  padding: 2px 6px; margin-left: auto; white-space: nowrap; }
.ps-ptcard-why strong, .ps-ptcard-step strong,
.ps-ptcard-opps strong { font-weight: 600; }
</style>
